import React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

// Step 8. The checklist, and the only place a product gets switched on.
//
// Publishing is gated on what the STORE requires, not on having walked the
// wizard. Each unmet item links to the step that fixes it -- except the print
// blocker, which lives in the legacy package admin, so that links there rather
// than being a dead end.
const FIXES = {
  season: 'basics',
  printess: 'basics',
  package: 'contents',
  variants: 'contents',
  pricing: 'schools',
  shipping: 'schools',
  schools: 'schools',
  backgrounds: 'presentation',
  styles: 'presentation',
};

const Review = ({
  product, readiness, steps, commit, busy, goTo,
}) => {
  const missing = readiness.checklist.filter((i) => !i.done);
  const stepKeys = new Set(steps.map((x) => x.key));

  return (
    <Stack spacing={2}>
      <List dense disablePadding>
        {readiness.checklist.map((item) => (
          <ListItem key={item.key} alignItems="flex-start" sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
              {item.done
                ? <CheckCircleIcon fontSize="small" color="success" />
                : <CancelIcon fontSize="small" color="error" />}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.done ? null : item.hint}
              primaryTypographyProps={{
                variant: 'body2',
                color: item.done ? 'text.primary' : 'error.main',
              }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        ))}
      </List>

      {readiness.warnings.map((w) => (
        <Alert key={w} severity="warning" variant="outlined">{w}</Alert>
      ))}

      {missing.length ? (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Not ready yet.</strong>
            {' The store hides a product until everything above is ticked, so it cannot be published.'}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {missing.map((item) => {
              // Prints are fixed on the legacy pricelist, which the wizard
              // cannot reach -- so link out rather than offer a dead button.
              if (item.key === 'prints') {
                return (
                  <Button
                    key={item.key}
                    variant="outlined"
                    href="/admin/packages"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Fix the prints on the pricelist
                  </Button>
                );
              }
              const target = FIXES[item.key];
              if (!target || !stepKeys.has(target)) return null;
              return (
                <Button key={item.key} variant="outlined" onClick={() => goTo(target)}>
                  {`Fix: ${item.label}`}
                </Button>
              );
            })}
          </Stack>
        </Alert>
      ) : null}

      {!missing.length && product.active ? (
        <Alert severity="success">
          <strong>Live.</strong>
          {' Customers at the assigned schools can buy this now.'}
        </Alert>
      ) : null}

      {!missing.length && !product.active ? (
        <Alert severity="success">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Ready to publish.</strong>
            {' Everything the store needs is in place — check the preview, then put it on sale.'}
          </Typography>
          <Button variant="contained" color="success" disabled={busy} onClick={() => commit({ activate: '1' })}>
            Publish — put it on sale
          </Button>
        </Alert>
      ) : null}
    </Stack>
  );
};

Review.propTypes = stepPropTypes;
Review.defaultProps = stepDefaultProps;

export default Review;
